/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Texture, utils } from 'pixi.js-legacy';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { IMAGE_PADDING } from '@appCanvasCommon/interfaces/image-padding.interface';
import {
	TicketContainer,
	TICKET_CHANGE_EVENTS,
	TicketLayout,
} from '@appPlanApplication/tickets/TicketContainer';
import { TicketComponentHelper } from '@appPlanApplication/ticket-components/TicketComponentHelper';
import { Side } from '@touchplan/common';
import { Ticket } from '@touchplan/common';
import { convertHexStringToNumber } from 'utils/color-utils';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { TICKET_TEXTURE_OPTIONS } from '../tickets/TICKET_TEXTURE_OPTIONS';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { cssVar } from '@touchplan/css-var';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

// Enum for positioning the constraint assigned icon
export enum ASSIGNED_ICON_SETTINGS {
	OFFSET_LEFT_ACTIVE = 74,
	OFFSET_TOP_ACTIVE = 0,

	OFFSET_LEFT_PULL = 126,
	OFFSET_TOP_PULL = 0,
}

export class ConstraintAssignedIcon extends TicketComponentHelper implements ComponentInterface {
	/**
	 * Stores the reference to the Pin FancySprite
	 */
	private _border: FancySprite;

	private _cachedColor: string;

	constructor(texture: Texture, canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super(texture, canvasAngularLand, ticketContainer);
		this.eventMode = EventMode.None;
		this._setupIcon();

		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		this.onChanged(ticketContainer);
	}

	/**
	 * Creates the Ticket Pin and Ticket Border and adds it as a child to the pin background
	 */
	private _setupIcon(): void {
		this._border = new FancySprite(
			utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_ASSIGNED_BORDER]
		);
		// Offset the border image to make it not disjointed
		this._border.x = -5;
		this._border.y = -6;
		this._border.visible = true;
		this._border.active = true;

		this.addChild(this._border);
	}

	private onChanged = (ticketContainer: TicketContainer) => {
		const data: Ticket = ticketContainer.getData();
		const layout = ticketContainer.getLayout();
		this.renderable = !!data.view.assignedProjectMember; // data.view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED; //
		if (this.renderable) {
			this._position(data, layout);

			const assignedRole = data.view.assignedProjectMemberRole;
			const color = assignedRole ? assignedRole.view.color : cssVar.black;

			// We don't want to calculate the color on every update if we don't need too...
			if (this._cachedColor !== color) {
				this.tint = convertHexStringToNumber(color);
				this._cachedColor = color;
			}
		}
	};

	private _position(data: Ticket, layout: TicketLayout): void {
		const newSide = data.view.side;

		if (newSide === Side.PULL) {
			this.width = 90;
			this.height = 90;

			this.x =
				layout.width / 2 -
				this.width / 2 +
				ASSIGNED_ICON_SETTINGS.OFFSET_LEFT_PULL -
				IMAGE_PADDING.ACTIVE_SHAPE;
			this.y = 0;
		} else {
			this.width = 49;
			this.height = 49;

			this.x =
				layout.width / 2 -
				this.width / 2 +
				ASSIGNED_ICON_SETTINGS.OFFSET_LEFT_ACTIVE -
				IMAGE_PADDING.ACTIVE_SHAPE;
			this.y = ASSIGNED_ICON_SETTINGS.OFFSET_TOP_ACTIVE;
		}
	}

	public override destroy(options?) {
		this._border.destroy();
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		super.destroy(options);
	}
}
